import { useState, useEffect, useCallback } from 'react';
import { misaService, MisaJob, MisaJobLog, MisaQueueStats, MisaSyncStats } from '../services/misa.service';

interface UseMisaJobsReturn {
  jobs: MisaJob[];
  stats: MisaSyncStats | null;
  loading: boolean;
  error: Error | null;
  refetch: () => void;
}

export const useMisaJobs = (tenantId: string): UseMisaJobsReturn => {
  const [jobs, setJobs] = useState<MisaJob[]>([]);
  const [stats, setStats] = useState<MisaSyncStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchJobs = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    setError(null);
    try {
      const response = await misaService.getJobStatuses(tenantId);
      setJobs(response.data);
      setStats(response.stats);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch MISA jobs'));
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  return {
    jobs,
    stats,
    loading,
    error,
    refetch: fetchJobs,
  };
};

interface UseMisaJobLogsReturn {
  logs: MisaJobLog[];
  loading: boolean;
  error: Error | null;
  meta: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  page: number;
  setPage: (page: number) => void;
  refetch: () => void;
}

export const useMisaJobLogs = (tenantId: string, jobType: string): UseMisaJobLogsReturn => {
  const [logs, setLogs] = useState<MisaJobLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });

  const fetchLogs = useCallback(async () => {
    if (!tenantId || !jobType) return;
    setLoading(true);
    setError(null);
    try {
      const response = await misaService.getJobLogs(tenantId, jobType, page);
      setLogs(response.data);
      setMeta(response.meta);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch MISA job logs'));
    } finally {
      setLoading(false);
    }
  }, [tenantId, jobType, page]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  return {
    logs,
    loading,
    error,
    meta,
    page,
    setPage,
    refetch: fetchLogs,
  };
};

interface UseMisaQueueStatsReturn {
  stats: MisaQueueStats | null;
  loading: boolean;
  error: Error | null;
}

export const useMisaQueueStats = (): UseMisaQueueStatsReturn => {
  const [stats, setStats] = useState<MisaQueueStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchStats = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await misaService.getQueueStats();
        setStats(data);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to fetch queue stats'));
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  return { stats, loading, error };
};
